// Configuration
const USE_EDGE_FUNCTION = true;//process.env.REACT_APP_USE_EDGE_FUNCTION === "true";
const EDGE_FUNCTION_URL = process.env.REACT_APP_EDGE_FUNCTION_URL || "";
// const DIRECT_API_URL = "https://api.anthropic.com/v1/messages";
// const API_KEY = process.env.REACT_APP_CLAUDE_API_KEY;

// const DIRECT_API_HEADERS = {
//   "Content-Type": "application/json",
//   "x-api-key": API_KEY,
//   "anthropic-version": "2023-06-01",
//   "anthropic-dangerous-direct-browser-access": "true",
// };

const EDGE_FUNCTION_HEADERS = {
  "Content-Type": "application/json",
  "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
};

export class ClaudeService {
  static async sendMessage({ messages, system, model = "claude-sonnet-4-6", maxTokens = 2000 }) {
    // Validate config at request time, not at module load
    if (USE_EDGE_FUNCTION && !EDGE_FUNCTION_URL) {
      console.error("Missing Edge Function URL. Set REACT_APP_EDGE_FUNCTION_URL in .env");
      return { success: false, error: "Edge Function URL not configured" };
    }
    // if (!USE_EDGE_FUNCTION && !API_KEY) {
    //   console.error("Missing Claude API key. Set REACT_APP_CLAUDE_API_KEY in .env");
    //   return { success: false, error: "Claude API key not configured" };
    // }
    try {
      const url = EDGE_FUNCTION_URL; ///USE_EDGE_FUNCTION ? EDGE_FUNCTION_URL : DIRECT_API_URL;
      const headers = EDGE_FUNCTION_HEADERS;//USE_EDGE_FUNCTION ? EDGE_FUNCTION_HEADERS : DIRECT_API_HEADERS;

      // Edge Function expects same format as direct API, so we can use the same body structure
      const body = JSON.stringify({
        model,
        ...(USE_EDGE_FUNCTION ? { maxTokens } : { max_tokens: maxTokens }), 
        system,
        messages: messages.map(m => ({
          role: m.role,
          content: m.content,
        })),
      });

      const response = await fetch(url, {
        method: "POST",
        headers,
        body,
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errBody.error?.message || `API error: ${response.status}`,
          status: response.status,
        };
      }

      const data = await response.json();

      // Edge Function returns { success, content, usage }, direct API returns { content: [{text}], usage }
      // Normalize the response format
      if (USE_EDGE_FUNCTION) {
        return {
          success: true,
          content: data.content || "",
          usage: data.usage,
        };
      } else {
        return {
          success: true,
          content: data.content?.[0]?.text || "",
          usage: data.usage,
        };
      }
    } catch (error) {
      console.error("Claude API error:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  static async sendMessageWithRetry(options, maxRetries = 2) {
    let lastError;
    for (let i = 0; i <= maxRetries; i++) {
      const result = await this.sendMessage(options);
      if (result.success) return result;
      lastError = result.error;
      if (i < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
    return { success: false, error: lastError };
  }

  static parseStructuredResponse(content) {
    try {
      const cleaned = content.replace(/```json|```/g, "").trim();
      return JSON.parse(cleaned);
    } catch (error) {
      // Full parse failed — try to salvage the chat_response text from the raw string.
      // This happens when generated HTML inside "content" contains unescaped double quotes
      // that break JSON.parse. We can still show the user a real reply.
      const match = content.match(/"chat_response"\s*:\s*"((?:[^"\\]|\\.)*)"/);
      if (match) {
        console.warn("JSON parse failed — recovering chat_response from raw string:", error.message);
        return {
          chat_response: match[1].replace(/\\n/g, "\n").replace(/\\"/g, '"'),
          surface_updates: [],
          desk_action: null,
        };
      }
      // No JSON structure at all — plain prose reply. Show the raw text verbatim so
      // Lumen's actual words reach the user instead of a generic fallback.
      const raw = content.replace(/```json|```/g, "").trim();
      if (raw) {
        console.warn("No JSON structure found — treating raw content as chat_response:", error.message);
        return { chat_response: raw, surface_updates: [], desk_action: null };
      }
      console.error("Failed to parse structured response and content is empty:", error);
      return {
        chat_response: "I had trouble reaching the server. Give me a moment and try again.",
        surface_updates: [],
        desk_action: null,
      };
    }
  }

  static _repairLiteralNewlines(raw) {
    let result = "";
    let inString = false;
    let escaped = false;
    for (let i = 0; i < raw.length; i++) {
      const ch = raw[i];
      if (escaped) {
        result += ch;
        escaped = false;
        continue;
      }
      if (ch === "\\") {
        escaped = true;
        result += ch;
        continue;
      }
      if (ch === '"') {
        inString = !inString;
        result += ch;
        continue;
      }
      if (inString && ch === "\n") { result += "\\n"; continue; }
      if (inString && ch === "\r") { result += "\\r"; continue; }
      result += ch;
    }
    return result;
  }

  static _extractLeadingJsonObject(raw) {
    const start = raw.indexOf("{");
    if (start === -1) return null;
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < raw.length; i++) {
      const ch = raw[i];
      if (escaped) { escaped = false; continue; }
      if (ch === "\\") { escaped = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === "{") depth++;
      else if (ch === "}" && --depth === 0) return raw.slice(start, i + 1);
    }
    return null;
  }

  static _cleanParse(content) {
    const cleaned = content.replace(/```json|```/g, "").trim();
    try {
      return { ok: true, data: JSON.parse(this._repairLiteralNewlines(cleaned)) };
    } catch (e) {
      // The model sometimes finishes the JSON and then keeps talking — a summary, a note,
      // stray punctuation. The envelope itself is fine, so take the first complete object
      // and drop the trailing text rather than discarding the whole reply (and with it any
      // surface_updates it carried).
      const sliced = this._extractLeadingJsonObject(cleaned);
      if (sliced && sliced !== cleaned) {
        try {
          const data = JSON.parse(this._repairLiteralNewlines(sliced));
          console.warn("[ClaudeService] Trailing text after JSON — recovered the envelope.");
          return { ok: true, data };
        } catch { /* fall through to the original error */ }
      }
      return { ok: false, error: e.message };
    }
  }

  static async sendStructuredMessage(options) {
    const result = await this.sendMessage(options);
    if (!result.success) return result;

    const attempt1 = this._cleanParse(result.content);
    if (attempt1.ok) {
      return { success: true, data: attempt1.data, raw: result.content, usage: result.usage };
    }

    // Parse failed — retry the API call once before giving up
    console.warn("[ClaudeService] JSON parse failed on first attempt, retrying:", attempt1.error);
    await new Promise(resolve => setTimeout(resolve, 800));

    const retry = await this.sendMessage(options);
    if (!retry.success) {
      return { success: false, error: `JSON parse failed and retry request also failed: ${retry.error}` };
    }

    const attempt2 = this._cleanParse(retry.content);
    if (attempt2.ok) {
      return { success: true, data: attempt2.data, raw: retry.content, usage: retry.usage };
    }

    console.error("[ClaudeService] JSON parse failed after retry:", attempt2.error, "\nContent:", retry.content?.slice(0, 300));

    const prose = (retry.content || "").replace(/```json|```/g, "").trim();

    const salvaged = prose.match(/"chat_response"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    if (salvaged) {
      console.warn("[ClaudeService] Unparseable envelope — recovered chat_response only.");
      return {
        success: true,
        data: {
          chat_response: salvaged[1].replace(/\\n/g, "\n").replace(/\\"/g, '"'),
          surface_updates: [],
          desk_action: null,
        },
        raw: retry.content,
        usage: retry.usage,
      };
    }

    if (prose && !prose.startsWith("{") && !prose.startsWith("[")) {
      console.warn("[ClaudeService] No JSON structure — treating raw content as chat_response.");
      return {
        success: true,
        data: { chat_response: prose, surface_updates: [], desk_action: null },
        raw: retry.content,
        usage: retry.usage,
      };
    }

    return { success: false, error: "Response was not valid JSON after retry — try again." };
  }
}

export default ClaudeService;
